import React, { useState } from "react";
import {
  Button,
  Drawer,
  Stack,
  TextField,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useDispatch } from "react-redux";
import {
  linkRestaurantInvoiceToHotelInvoice,
  listRestaurantInvoiceDetails,
  searchHotelBookings,
} from "../../../store/actions";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

const LinkBookingInvoiceButton = ({ colors }) => {
  const { id } = useParams();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    booking: null,
  });

  const [bookingId, setBookingId] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [checkinDate, setCheckinDate] = useState("");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🔹 Reset all state when Drawer closes
  const handleCloseDrawer = () => {
    setOpen(false);
    setBookingId("");
    setRoomNumber("");
    setCheckinDate("");
    setCheckoutDate("");
    setResults([]);
    setConfirmDialog({ open: false, booking: null });
    setLoading(false);
  };

  const handleFilter = async () => {
    if (
      checkoutDate &&
      checkinDate &&
      moment(checkoutDate).isBefore(moment(checkinDate))
    ) {
      toast.warning("Checkout date cannot be earlier than check-in date.");
      return;
    }

    setLoading(true);
    try {
      // 🔹 Updated to use searchHotelBookings
      const res = await dispatch(
        searchHotelBookings({
          booking_id: bookingId || null,
          room_number: roomNumber || null,
          checkin_date: checkinDate ? moment(checkinDate).unix() : null,
          checkout_date: checkoutDate ? moment(checkoutDate).unix() : null,
        })
      );
      setResults(res || []);
    } catch (error) {
      console.error("Error searching bookings:", error);
      toast.error("Failed to fetch bookings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (booking) => {
    setConfirmDialog({ open: true, booking });
  };

  const confirmLink = async () => {
    const { booking } = confirmDialog;
    try {
      const res = await dispatch(
        linkRestaurantInvoiceToHotelInvoice({
          bookingId: booking.booking_id,
          restaurantInvoiceId: id,
        })
      );
      toast.success(`Booking ${booking.booking_id} linked successfully!`);
      dispatch(listRestaurantInvoiceDetails({ id }));
      handleCloseDrawer();
    } catch (err) {
      toast.error("Failed to link booking.");
    } finally {
      setConfirmDialog({ open: false, booking: null });
    }
  };

  const getRoomNumbers = (mappings) => {
    if (!mappings || mappings.length === 0) return "No Room";
    return mappings.map((m) => m.actual_room_no).join(", ");
  };

  return (
    <>
      <Button
        variant="contained"
        color="info"
        sx={{
          backgroundColor: colors?.blueAccent?.[700],
          "&:hover": { backgroundColor: colors?.blueAccent?.[800] },
        }}
        startIcon={<LinkIcon />}
        onClick={() => setOpen(true)}
      >
        Link Booking Invoice
      </Button>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            p: 2,
            bgcolor: theme.palette.background.default,
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Find Booking Invoice
          </Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Filters */}
        <Grid container spacing={1} mb={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Booking ID"
              size="small"
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Room Number"
              size="small"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Check-in After"
              type="date"
              size="small"
              value={checkinDate}
              onChange={(e) => setCheckinDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter:
                    theme.palette.mode === "dark"
                      ? "invert(1)" // white in dark mode
                      : "invert(0)", // normal in light mode
                },
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Check-out Before"
              type="date"
              size="small"
              value={checkoutDate}
              onChange={(e) => setCheckoutDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter:
                    theme.palette.mode === "dark"
                      ? "invert(1)" // white in dark mode
                      : "invert(0)", // normal in light mode
                },
              }}
            />
          </Grid>
          <Grid item xs={12} textAlign="right">
            <Button
              variant="contained"
              onClick={handleFilter}
              disabled={loading}
              sx={{
                backgroundColor: colors?.blueAccent?.[700],
                "&:hover": { backgroundColor: colors?.blueAccent?.[800] },
              }}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Results */}
        {results.length > 0 ? (
          <List>
            {results.map((r) => (
              <React.Fragment key={r._id}>
                <ListItem
                  button
                  onClick={() => handleSelect(r)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    bgcolor:
                      r.process_status === "checked_in"
                        ? `${theme.palette.success.light}33`
                        : `${theme.palette.warning.light}22`,
                    "&:hover": { bgcolor: theme.palette.action.hover },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight={600}>
                        {r.guest_name} — Room {getRoomNumbers(r.room_mappings)}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">
                          Booking ID: {r.booking_id}
                        </Typography>
                        {/* <Typography variant="body2">
                          Meal Plan: {r.meal_plan || "N/A"}
                        </Typography> */}
                        <Typography variant="body2">
                          Check-in:{" "}
                          {r.checkin_date
                            ? moment.unix(r.checkin_date).format("DD MMM YYYY")
                            : "-"}{" "}
                          | Check-out:{" "}
                          {r.checkout_date
                            ? moment.unix(r.checkout_date).format("DD MMM YYYY")
                            : "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: {r.process_status?.replace("_", " ")}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        ) : (
          !loading && (
            <Box textAlign="center" color="text.secondary" mt={4}>
              <Typography variant="body2">No results found.</Typography>
            </Box>
          )
        )}
      </Drawer>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, booking: null })}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Link To Booking Invoice
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to link this bill to the following guest?
          </Typography>

          {/* Styled Summary Box */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: theme.palette.action.hover,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" color="primary" fontWeight={600}>
                Room {getRoomNumbers(confirmDialog.booking?.room_mappings)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  textTransform: "uppercase",
                  fontWeight: 600,
                  opacity: 0.7,
                }}
              >
                {confirmDialog.booking?.process_status?.replace("_", " ")}
              </Typography>
            </Stack>

            <Typography variant="subtitle1" fontWeight={600} mt={0.5}>
              {confirmDialog.booking?.guest_name}
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Booking ID: {confirmDialog.booking?.booking_id}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            * This will post the restaurant bill amount to the hotel bill.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            * GST will be recalculated to match the booking's room slab.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, booking: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmLink}
            variant="contained"
            // ✅ Strict usage of colors prop for Confirmation button
            sx={{
              backgroundColor: colors?.blueAccent?.[700] || "info.main",
              "&:hover": {
                backgroundColor: colors?.blueAccent?.[800] || "info.dark",
              },
              px: 3,
            }}
          >
            Confirm Link
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkBookingInvoiceButton;
